/**
 * WebSocket client service for real-time communication
 */

import { store } from '../store';

export interface WebSocketMessage {
  type: string;
  data?: any;
  channel?: string;
  timestamp?: string;
  message_id?: string;
  broadcast_id?: string;
}

export interface WebSocketConfig {
  url: string;
  token: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  heartbeatInterval?: number;
}

export class WebSocketService {
  private ws: WebSocket | null = null;
  private config: WebSocketConfig;
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private isConnecting = false;
  private isManualClose = false;
  private messageHandlers: Map<string, ((message: WebSocketMessage) => void)[]> = new Map();
  private connectionListeners: ((connected: boolean) => void)[] = [];

  constructor(config: WebSocketConfig) {
    this.config = {
      reconnectInterval: 5000,
      maxReconnectAttempts: 10,
      heartbeatInterval: 30000,
      ...config,
    };
  }

  /**
   * Connect to WebSocket server
   */
  public async connect(): Promise<void> {
    // Prevent multiple simultaneous connections
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }
    
    if (this.isConnecting) {
      console.log('WebSocket connection already in progress');
      return;
    }

    // Close any existing connection first
    if (this.ws && this.ws.readyState !== WebSocket.CLOSED) {
      console.log('Closing existing WebSocket connection');
      this.ws.close();
      this.ws = null;
    }

    this.isConnecting = true;
    this.isManualClose = false;

    try {
      const wsUrl = `${this.config.url}?token=${encodeURIComponent(this.config.token)}`;
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
      this.ws.onerror = this.handleError.bind(this);

      console.log('WebSocket connecting to:', wsUrl.replace(/token=[^&]+/, 'token=***'));
    } catch (error) {
      console.error('WebSocket connection error:', error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  public disconnect(): void {
    this.isManualClose = true;
    this.clearTimers();
    
    if (this.ws) {
      this.ws.close(1000, 'Manual disconnect');
      this.ws = null;
    }
    
    this.notifyConnectionListeners(false);
  }

  /**
   * Send message to server
   */
  public send(message: WebSocketMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not connected, message not sent:', message);
    }
  }

  /**
   * Subscribe to a channel
   */
  public subscribe(channel: string): void {
    this.send({
      type: 'subscribe',
      data: { channel }
    });
  }

  /**
   * Unsubscribe from a channel
   */
  public unsubscribe(channel: string): void {
    this.send({
      type: 'unsubscribe',
      data: { channel }
    });
  }

  /**
   * Add message handler for specific message type
   */
  public onMessage(type: string, handler: (message: WebSocketMessage) => void): void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, []);
    }
    this.messageHandlers.get(type)!.push(handler);
  }

  /**
   * Remove message handler
   */
  public offMessage(type: string, handler: (message: WebSocketMessage) => void): void {
    const handlers = this.messageHandlers.get(type);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Add connection status listener
   */
  public onConnection(listener: (connected: boolean) => void): void {
    this.connectionListeners.push(listener);
  }

  /**
   * Remove connection status listener
   */
  public offConnection(listener: (connected: boolean) => void): void {
    const index = this.connectionListeners.indexOf(listener);
    if (index > -1) {
      this.connectionListeners.splice(index, 1);
    }
  }

  /**
   * Get connection status
   */
  public isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Handle WebSocket open event
   */
  private handleOpen(): void {
    console.log('WebSocket connected');
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.startHeartbeat();
    this.notifyConnectionListeners(true);
  }

  /**
   * Handle WebSocket message event
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const message: WebSocketMessage = JSON.parse(event.data);
      console.log('WebSocket message received:', message);

      // Handle built-in message types
      switch (message.type) {
        case 'connection_established':
          console.log('WebSocket connection established:', message.data);
          break;
        case 'heartbeat':
          this.send({ type: 'heartbeat_ack' });
          break;
        case 'heartbeat_ack':
          // Heartbeat acknowledged
          break;
        case 'subscription_confirmed':
          console.log('Subscribed to channel:', message.data?.channel);
          break;
        case 'subscription_error':
          console.error('Subscription error:', message.data?.message);
          break;
        case 'rate_limit_exceeded':
          console.warn('Rate limit exceeded:', message.data?.message);
          break;
        case 'error':
          console.error('WebSocket error:', message.data?.message);
          break;
      }

      // Notify registered handlers
      const handlers = this.messageHandlers.get(message.type);
      if (handlers) {
        handlers.forEach(handler => {
          try {
            handler(message);
          } catch (error) {
            console.error('Error in message handler:', error);
          }
        });
      }

      // Notify global handlers
      const globalHandlers = this.messageHandlers.get('*');
      if (globalHandlers) {
        globalHandlers.forEach(handler => {
          try {
            handler(message);
          } catch (error) {
            console.error('Error in global message handler:', error);
          }
        });
      }

    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }

  /**
   * Handle WebSocket close event
   */
  private handleClose(event: CloseEvent): void {
    console.log('WebSocket closed:', event.code, event.reason);
    this.isConnecting = false;
    this.clearTimers();
    this.notifyConnectionListeners(false);

    if (!this.isManualClose) {
      this.scheduleReconnect();
    }
  }

  /**
   * Handle WebSocket error event
   */
  private handleError(event: Event): void {
    console.error('WebSocket error:', event);
    this.isConnecting = false;
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.isManualClose || this.reconnectAttempts >= this.config.maxReconnectAttempts!) {
      console.log('Max reconnection attempts reached or manual close');
      return;
    }

    const delay = Math.min(
      this.config.reconnectInterval! * Math.pow(2, this.reconnectAttempts),
      30000 // Max 30 seconds
    );

    console.log(`Scheduling reconnection attempt ${this.reconnectAttempts + 1} in ${delay}ms`);

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  /**
   * Start heartbeat mechanism
   */
  private startHeartbeat(): void {
    this.heartbeatTimer = window.setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.send({ type: 'ping' });
      }
    }, this.config.heartbeatInterval!);
  }

  /**
   * Clear all timers
   */
  private clearTimers(): void {
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.heartbeatTimer) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Notify connection listeners
   */
  private notifyConnectionListeners(connected: boolean): void {
    this.connectionListeners.forEach(listener => {
      try {
        listener(connected);
      } catch (error) {
        console.error('Error in connection listener:', error);
      }
    });
  }
}

// WebSocket service instance
let wsService: WebSocketService | null = null;
let isInitializing = false;

/**
 * Initialize WebSocket service
 */
export function initializeWebSocket(token: string): WebSocketService {
  // Prevent multiple simultaneous initializations
  if (wsService) {
    console.log('WebSocket service already exists, returning existing instance');
    return wsService;
  }
  
  if (isInitializing) {
    console.log('WebSocket service initialization in progress');
    // Return existing service if available, otherwise wait
    return wsService || new WebSocketService({
      url: import.meta.env.VITE_WS_URL || 'ws://localhost:8000/api/v1/ws',
      token,
    });
  }

  isInitializing = true;
  console.log('Initializing new WebSocket service');
  
  const wsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:8000/api/v1/ws';
  
  wsService = new WebSocketService({
    url: wsUrl,
    token,
  });

  isInitializing = false;
  return wsService;
}

/**
 * Get WebSocket service instance
 */
export function getWebSocketService(): WebSocketService | null {
  return wsService;
}

/**
 * Cleanup WebSocket service
 */
export function cleanupWebSocket(): void {
  if (wsService) {
    wsService.disconnect();
    wsService = null;
  }
}
